
import React, { useState } from 'react';
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Share2, Calendar } from 'lucide-react';
import { toast } from 'sonner';

export interface Share {
  id: number;
  user_id: string;
  points: number;
  platform: string;
  share_link: string;
  success: boolean;
  created_at: string;
  Platform?: string;
  Share_Link?: string;
  Points?: number;
  User_id?: string;
}

interface SharesTabProps {
  shares: Share[];
  userId: string | null;
}

const SharesTab: React.FC<SharesTabProps> = ({ shares, userId }) => {
  const [isLoading, setIsLoading] = useState(false);

  if (!userId) {
    return (
      <div className="p-4 bg-black/20 rounded-lg">
        <p className="text-center">Please log in to view your shares.</p>
      </div>
    );
  }

  if (shares.length === 0) {
    return (
      <div className="p-4 bg-black/20 rounded-lg text-center">
        <p className="mb-4">You haven't shared any videos yet.</p>
        <Button onClick={() => toast.info("Record and share videos to earn points!")}>
          Record a Video
        </Button>
      </div>
    );
  }

  // Normalize the data structure to handle both capitalized and lowercase field names
  const normalizeShare = (share: Share): Share => {
    return {
      id: share.id,
      user_id: share.user_id || share.User_id || '',
      points: share.points || share.Points || 0,
      platform: share.platform || share.Platform || '',
      share_link: share.share_link || share.Share_Link || '',
      success: share.success || false,
      created_at: share.created_at || new Date().toISOString()
    };
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-bold">Your Social Shares</h2>
        <Button variant="outline" size="sm" disabled={isLoading}>
          <Share2 className="h-4 w-4 mr-2" />
          Share Again
        </Button>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {shares.map(share => {
          const normalizedShare = normalizeShare(share);
          return (
            <Card key={normalizedShare.id} className="border border-gray-700 bg-gray-900 text-white overflow-hidden">
              <CardContent className="p-4">
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="font-medium mb-1">
                      Shared on {normalizedShare.platform}
                    </h3>
                    <p className="text-sm text-gray-400 flex items-center">
                      <Calendar className="h-3 w-3 mr-1" />
                      {new Date(normalizedShare.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="bg-yellow-900 px-2 py-1 rounded text-yellow-400 text-sm">
                    +{normalizedShare.points} points
                  </div>
                </div>
                <div className="mt-3 text-sm">
                  <a 
                    href={normalizedShare.share_link} 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="text-blue-400 hover:underline truncate block"
                  >
                    {normalizedShare.share_link}
                  </a>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
};

export default SharesTab;
